export const BRL = (v) =>
  (v ?? 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const PCT = (v) => `${(v ?? 0).toFixed(1)}%`;

// LGPD: maskCpf removed — personal documents are no longer stored or displayed.

export const maskCnpj = (cnpj) => {
  if (!cnpj) return "";
  const c = String(cnpj).replace(/\D/g, "");
  if (c.length !== 14) return cnpj;
  return `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12)}`;
};

export const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    // If already in DD/MM/AAAA, return as-is
    if (typeof iso === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(iso)) return iso;
    // If YYYY-MM-DD (date-only, no time), format directly to avoid TZ shift
    if (typeof iso === "string") {
      const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    }
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleDateString("pt-BR");
  } catch {
    return String(iso);
  }
};

// ==================== Date helpers (DD/MM/AAAA) ====================
// Only digits
const _onlyDigits = (s) => String(s || "").replace(/\D/g, "");

// Apply DD/MM/AAAA mask progressively while user types
export const maskDateBR = (input) => {
  const d = _onlyDigits(input).slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};

// Check if a DD/MM/AAAA string represents a real calendar date
export const isValidBRDate = (str) => {
  if (!str) return false;
  const m = String(str).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return false;
  const dd = parseInt(m[1], 10);
  const mm = parseInt(m[2], 10);
  const yyyy = parseInt(m[3], 10);
  if (mm < 1 || mm > 12) return false;
  if (dd < 1 || dd > 31) return false;
  if (yyyy < 1900 || yyyy > 2100) return false;
  const d = new Date(yyyy, mm - 1, dd);
  return (
    d.getFullYear() === yyyy &&
    d.getMonth() === mm - 1 &&
    d.getDate() === dd
  );
};

// DD/MM/AAAA -> YYYY-MM-DD (returns "" if invalid/empty)
export const brToIso = (br) => {
  if (!br) return "";
  const m = String(br).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
};

// YYYY-MM-DD (or ISO) -> DD/MM/AAAA (returns "" if empty/invalid)
export const isoToBr = (iso) => {
  if (!iso) return "";
  const s = String(iso);
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  return `${dd}/${mm}/${yy}`;
};

export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export const occupancyTone = (pct) => {
  if (pct > 100) return { color: "#8B5CF6", label: "Excedido", bg: "bg-violet-50", text: "text-violet-700", ring: "ring-violet-200" };
  if (pct === 100) return { color: "#22C55E", label: "Completo", bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200" };
  if (pct >= 80) return { color: "#EAB308", label: "Atenção", bg: "bg-amber-50", text: "text-amber-700", ring: "ring-amber-200" };
  if (pct > 0) return { color: "#EF4444", label: "Déficit", bg: "bg-red-50", text: "text-red-700", ring: "ring-red-200" };
  return { color: "#8F9D97", label: "Sem quadro", bg: "bg-slate-50", text: "text-slate-600", ring: "ring-slate-200" };
};

export const statusBadge = (st) => {
  const map = {
    ativo: { cls: "bg-emerald-100 text-emerald-800 ring-emerald-200", label: "Ativo" },
    ferias: { cls: "bg-blue-100 text-blue-800 ring-blue-200", label: "Férias" },
    afastado: { cls: "bg-amber-100 text-amber-800 ring-amber-200", label: "Afastado" },
    em_experiencia: { cls: "bg-violet-100 text-violet-800 ring-violet-200", label: "Em experiência" },
    desligado: { cls: "bg-slate-100 text-slate-700 ring-slate-200", label: "Desligado" },
    transferido: { cls: "bg-cyan-100 text-cyan-800 ring-cyan-200", label: "Transferido" },
  };
  return map[st] || { cls: "bg-slate-100 text-slate-700 ring-slate-200", label: st || "—" };
};

// Tipo Operacional — taxonomia oficial (4 categorias) + cor de marca
// Produção=azul · Manutenção=laranja · Administrativo=cinza · Processo=verde
export const tipoOperacionalMeta = (tipo) => {
  const map = {
    Producao:       { label: "Produção",       cls: "bg-blue-50 border-blue-200 text-blue-800",       dot: "bg-blue-500" },
    Manutencao:     { label: "Manutenção",     cls: "bg-orange-50 border-orange-200 text-orange-800", dot: "bg-orange-500" },
    Administrativo: { label: "Administrativo", cls: "bg-slate-100 border-slate-200 text-slate-700",   dot: "bg-slate-500" },
    Processo:       { label: "Processo",       cls: "bg-emerald-50 border-emerald-200 text-emerald-800", dot: "bg-emerald-500" },
  };
  return map[tipo] || { label: tipo || "—", cls: "bg-slate-50 border-slate-200 text-slate-600", dot: "bg-slate-300" };
};

export const TIPO_OPERACIONAL_OPTIONS = [
  { value: "Producao", label: "Produção" },
  { value: "Manutencao", label: "Manutenção" },
  { value: "Administrativo", label: "Administrativo" },
  { value: "Processo", label: "Processo" },
];

